/**
 * From an expenditure and a goal to daily calorie and macro targets.
 *
 * Calories: expenditure + 1100 × rate, since 7700 kcal/kg ÷ 7 days = 1100 kcal a
 * day per kg/week. The rate is bounded (lose ≤ 1% of body weight a week, gain
 * ≤ 0.5%) and calories are floored (1200 women / 1500 men), with a warning when
 * the target sits under BMR. Protein is set per kg of body weight, fat as a share
 * of calories by diet style (never under 0.5 g/kg), and carbs take the rest.
 */

import { ageOn } from './days.ts'

export type Sex = 'male' | 'female'
export type Activity = 'sedentary' | 'light' | 'moderate' | 'active' | 'very'
export type GoalType = 'lose' | 'maintain' | 'gain'
export type ProteinPref = 'low' | 'medium' | 'high'
export type DietStyle = 'balanced' | 'lowfat' | 'lowcarb' | 'keto'

export const ACTIVITY: Record<Activity, { factor: number; label: string; hint: string }> = {
  sedentary: { factor: 1.2, label: 'Sedentary', hint: 'Desk job, little exercise' },
  light: { factor: 1.375, label: 'Light', hint: 'Exercise 1–3 days a week' },
  moderate: { factor: 1.55, label: 'Moderate', hint: 'Exercise 3–5 days a week' },
  active: { factor: 1.725, label: 'Active', hint: 'Hard exercise 6–7 days a week' },
  very: { factor: 1.9, label: 'Very active', hint: 'Physical job plus training' },
}

export const PROTEIN_G_PER_KG: Record<ProteinPref, number> = { low: 1.6, medium: 2.0, high: 2.4 }
export const FAT_SHARE: Record<DietStyle, number> = { balanced: 0.3, lowfat: 0.2, lowcarb: 0.4, keto: 0.7 }
export const KCAL_PER_KG_WEEK_PER_DAY = 1100

export type ProfileLike = {
  sex?: Sex | null
  birth_date?: string | null
  height_cm?: number | null
  activity?: Activity | null
  goal_type?: GoalType | null
  goal_rate_kg_wk?: number | null
  goal_weight_kg?: number | null
  protein_pref?: ProteinPref | null
  diet_style?: DietStyle | null
}

export function isProfileComplete(p: ProfileLike | null | undefined): p is Required<ProfileLike> {
  return !!(p && p.sex && p.birth_date && p.height_cm && p.activity && p.goal_type)
}

/** Mifflin-St Jeor. */
export function bmr(sex: Sex, kg: number, cm: number, age: number): number {
  return 10 * kg + 6.25 * cm - 5 * age + (sex === 'male' ? 5 : -161)
}

export function bmrFor(p: ProfileLike, kg: number, today: string): number {
  const age = p.birth_date ? ageOn(p.birth_date, today) : 35
  return bmr(p.sex ?? 'female', kg, p.height_cm ?? 170, age)
}

export function priorExpenditure(p: ProfileLike, kg: number, today: string): number {
  return bmrFor(p, kg, today) * ACTIVITY[p.activity ?? 'light'].factor
}

/** The signed weekly rate actually used: loss negative, clamped to safe bounds. */
export function effectiveRate(p: ProfileLike, kg: number): number {
  if (p.goal_type === 'maintain' || !p.goal_type) return 0
  const r = Math.abs(p.goal_rate_kg_wk ?? 0)
  if (p.goal_type === 'lose') return -Math.min(r, 0.01 * kg)
  return Math.min(r, 0.005 * kg)
}

export function rateBounds(kg: number): { maxLose: number; maxGain: number } {
  return { maxLose: Math.round(0.01 * kg * 100) / 100, maxGain: Math.round(0.005 * kg * 100) / 100 }
}

export type Proposal = {
  kcal: number; protein_g: number; carbs_g: number; fat_g: number; fibre_g: number
  rate: number; warnings: string[]
}

const r5 = (v: number) => Math.round(v / 5) * 5
const r10 = (v: number) => Math.round(v / 10) * 10

export function macrosFor(kcal: number, p: ProfileLike, kg: number) {
  const warnings: string[] = []
  // Protein on goal weight when cutting toward one, but never below 80% of
  // current — a 140 kg person aiming for 90 kg still needs more than 90 kg's worth.
  const basis = p.goal_type === 'lose' && p.goal_weight_kg
    ? Math.max(p.goal_weight_kg, 0.8 * kg) : kg
  let protein = r5(PROTEIN_G_PER_KG[p.protein_pref ?? 'medium'] * basis)
  const style = p.diet_style ?? 'balanced'
  let fat = r5(Math.max(0.5 * kg, (FAT_SHARE[style] * kcal) / 9))
  let carbs = (kcal - protein * 4 - fat * 9) / 4
  if (style === 'keto' && carbs > 30) {
    fat = r5(fat + ((carbs - 30) * 4) / 9)
    carbs = 30
  }
  if (carbs < 0) {
    fat = r5(Math.max(0.5 * kg, (kcal - protein * 4) / 9))
    carbs = (kcal - protein * 4 - fat * 9) / 4
    if (carbs < 0) {
      protein = r5(Math.max(0, (kcal - fat * 9) / 4))
      carbs = 0
      warnings.push('Calories are too low to fit the protein and fat minimums.')
    }
  }
  return { protein_g: protein, carbs_g: Math.max(0, r5(carbs)), fat_g: fat, warnings }
}

export function propose(opts: {
  expenditure: number; kg: number; profile: ProfileLike; today: string
}): Proposal {
  const { expenditure, kg, profile, today } = opts
  const warnings: string[] = []
  const rate = effectiveRate(profile, kg)
  if (profile.goal_rate_kg_wk && Math.abs(rate) < Math.abs(profile.goal_rate_kg_wk) - 1e-9) {
    warnings.push(`Rate capped at ${Math.abs(rate).toFixed(2)} kg/week for your weight.`)
  }
  let kcal = r10(expenditure + KCAL_PER_KG_WEEK_PER_DAY * rate)
  const floor = profile.sex === 'male' ? 1500 : 1200
  if (kcal < floor) {
    kcal = floor
    warnings.push(`Held at the ${floor} kcal minimum.`)
  }
  const b = bmrFor(profile, kg, today)
  if (kcal < b) warnings.push('This is below your estimated BMR. Consider a slower rate.')
  const m = macrosFor(kcal, profile, kg)
  return {
    kcal, ...m, fibre_g: Math.round((14 * kcal) / 1000), rate,
    warnings: [...warnings, ...m.warnings],
  }
}

/** Weekly check-in: move toward the proposal, at most ±200 kcal a week, and
 *  hold when the week's data is too thin to trust. */
export function checkinProposal(opts: {
  current: { kcal: number } | null
  proposal: Proposal
  loggedDays: number
  weighIns: number
  profile: ProfileLike
  kg: number
}): { kind: 'change' | 'keep'; kcal: number; reason: string; macros: ReturnType<typeof macrosFor> } {
  const { current, proposal, loggedDays, weighIns, profile, kg } = opts
  if (current && (loggedDays < 4 || weighIns < 3)) {
    const why = loggedDays < 4
      ? `only ${loggedDays} complete day${loggedDays === 1 ? '' : 's'} logged`
      : `only ${weighIns} weigh-in${weighIns === 1 ? '' : 's'}`
    return { kind: 'keep', kcal: current.kcal, macros: macrosFor(current.kcal, profile, kg),
      reason: `Keeping your target — ${why} this week isn't enough to adjust on.` }
  }
  if (!current) return { kind: 'change', kcal: proposal.kcal, macros: macrosFor(proposal.kcal, profile, kg),
    reason: 'Your first target.' }
  const delta = Math.max(-200, Math.min(200, proposal.kcal - current.kcal))
  const kcal = r10(current.kcal + delta)
  if (Math.abs(kcal - current.kcal) < 30) {
    return { kind: 'keep', kcal: current.kcal, macros: macrosFor(current.kcal, profile, kg),
      reason: 'You\'re on track. No change needed.' }
  }
  const capped = delta !== proposal.kcal - current.kcal
  return { kind: 'change', kcal, macros: macrosFor(kcal, profile, kg),
    reason: `${kcal > current.kcal ? 'Up' : 'Down'} ${Math.abs(kcal - current.kcal)} kcal` +
      (capped ? ' (changes are limited to 200 kcal a week).' : '.') }
}

/** Weeks until the goal weight at the current trend rate, or null. */
export function weeksToGoal(kg: number, goal: number | null | undefined, ratePerWeek: number | null): number | null {
  if (!goal || !ratePerWeek) return null
  const diff = goal - kg
  if (Math.abs(diff) < 0.3) return 0
  if (Math.sign(diff) !== Math.sign(ratePerWeek)) return null
  return Math.ceil(diff / ratePerWeek)
}
